import React, { RefObject, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { FormValidationState } from 'utils';

import AutocompleteAddressInput from 'components/autocomplete-address-input';
import { FLOATING_LABEL_INPUT_WRAPPER_MARGIN } from 'components/floating-label-fields/styled/base';
import IconClose from 'components/icons/close';
import useMediaQuery from 'hooks/use-media-query';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { IPhoneAddressValidation } from '../types';

import theme from './theme';

const DeliveryAddressWrapper = styled.div`
  position: relative;
`;

const DeliveryAddressInput = styled(AutocompleteAddressInput)`
  z-index: ${Styles.zIndex.below};
`;

const DeliveryAddressClearButton = styled.button`
  background: ${Styles.color.white};
  border: none;
  border-radius: 0 ${Styles.borderRadius} ${Styles.borderRadius} 0;
  cursor: pointer;
  width: 3rem;
  position: absolute;
  right: ${theme.buttonRight};
  top: 1px;
  bottom: 1px;
  z-index: ${Styles.zIndex.normal}; /* fixes shadow bug */
  ${FLOATING_LABEL_INPUT_WRAPPER_MARGIN}
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    fill: ${Styles.color.grey.one};
    width: 1.25rem;
  }
`;

interface IDeliveryAddressFormInputProps {
  setAddressString: (address: string) => void;
  setPlaceId: (placeId: string) => void;
  setValidation: (validation: IPhoneAddressValidation) => void;
  setMessage: (message: string) => void;
  unitRef: RefObject<HTMLInputElement>;
  validation: IPhoneAddressValidation;
  setCoordinates: (coordinates: undefined) => void;
  addressString: string;
  message: string;
}

const DeliveryAddressFormInput: React.FC<IDeliveryAddressFormInputProps> = ({
  setAddressString,
  setPlaceId,
  setValidation,
  setMessage,
  unitRef,
  validation,
  setCoordinates,
  addressString,
  message,
}) => {
  const { formatMessage } = useIntl();
  const isDesktop = useMediaQuery('desktop');
  const deliveryAddressInputRef = useRef<HTMLInputElement>(null);

  const clearAddress = () => {
    setAddressString('');
    setPlaceId('');
  };

  const enableSimplifiedDelivery = useFlag(LaunchDarklyFlag.ENABLE_SIMPLE_DELIVERY_FORM);

  const handleAddressChange = (newAddressString: string, id: string) => {
    setAddressString(newAddressString);
    if (!id) {
      setValidation({
        ...validation,
        address: FormValidationState.INVALID,
      });
      setMessage(formatMessage({ id: 'addressError' }));
      return;
    }

    if (unitRef.current) {
      unitRef.current.focus();
    }

    setValidation({
      ...validation,
      address: FormValidationState.PRISTINE,
    });
    setMessage('');
    setPlaceId(id);
    setCoordinates(undefined);
  };

  useEffect(() => {
    if (!addressString && deliveryAddressInputRef.current) {
      deliveryAddressInputRef.current.focus();
    }
  }, [addressString]);

  return (
    <DeliveryAddressWrapper style={{ margin: enableSimplifiedDelivery ? '2rem 0' : '' }}>
      <DeliveryAddressInput
        data-testid="delivery-address-input"
        label={formatMessage({ id: 'deliveryAddress' })}
        required
        matchTypes={['address']}
        forceHideOnClickOutside={isDesktop}
        ref={deliveryAddressInputRef}
        onAddressChange={handleAddressChange}
        initialValue={addressString}
        validation={validation.address}
        message={message}
        value={addressString}
      />
      {addressString.length > 0 && (
        <DeliveryAddressClearButton
          type="button"
          aria-label={formatMessage({ id: 'clearText' })}
          onClick={clearAddress}
        >
          <IconClose />
        </DeliveryAddressClearButton>
      )}
    </DeliveryAddressWrapper>
  );
};

export default DeliveryAddressFormInput;
